import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { teamApi, rolesApi, workflowsApi, adminApi, clientsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Shield, Trash2, UserCog, Plus, UserPlus, Pencil, GitBranch, X, ArrowRight, Camera, Copy, Check, Eye, EyeOff, KeyRound } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const ALL_PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'clients', label: 'Clientes' },
  { key: 'projects', label: 'Proyectos' },
  { key: 'tasks', label: 'Tareas' },
  { key: 'content', label: 'Contenido' },
  { key: 'documents', label: 'Documentos' },
  { key: 'work_orders', label: 'Ordenes de Trabajo' },
  { key: 'chat', label: 'Chat Interno' },
  { key: 'invoices', label: 'Facturas' },
  { key: 'finances', label: 'Finanzas' },
  { key: 'ai', label: 'Estudio IA' },
  { key: 'team', label: 'Equipo' },
  { key: 'performance', label: 'Desempeño' },
];

const TeamPage = () => {
  const [team, setTeam] = useState([]);
  const [roles, setRoles] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', username: '', password: '', role: 'team', position: '', associated_client_id: '' });
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('team'); // team, roles, workflow
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', display_name: '', description: '', permissions: [] });
  // Workflow state
  const [workflows, setWorkflows] = useState([]);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [workflowForm, setWorkflowForm] = useState({ name: '', steps: [] });
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [workflowTasks, setWorkflowTasks] = useState({});
  const photoInputRef = useRef(null);
  const [uploadingForUser, setUploadingForUser] = useState(null);
  const [credentialsDialog, setCredentialsDialog] = useState({ open: false, email: '', password: '', name: '', isReset: false });
  const [copiedField, setCopiedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '', role: 'team', position: '' });

  const API_URL = '';

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingForUser) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', 'user');
    formData.append('entity_id', uploadingForUser);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/upload-profile-photo`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (res.ok) { toast.success('Foto actualizada'); fetchTeam(); }
      else toast.error('Error al subir foto');
    } catch { toast.error('Error al subir foto'); }
    setUploadingForUser(null);
  };

  useEffect(() => { fetchTeam(); fetchRoles(); fetchWorkflows(); fetchClients(); }, []);

  const fetchTeam = async () => {
    try { const r = await teamApi.getAll(); setTeam(r.data); } catch { toast.error('Error al cargar equipo'); } finally { setLoading(false); }
  };
  const fetchRoles = async () => { try { const r = await rolesApi.getAll(); setRoles(r.data); } catch {} };
  const fetchWorkflows = async () => {
    try {
      const r = await workflowsApi.getAll();
      setWorkflows(r.data);
      // Fetch tasks for each workflow
      const tasksMap = {};
      for (const wf of r.data) {
        try {
          const tr = await workflowsApi.getTasks(wf.workflow_id);
          tasksMap[wf.workflow_id] = tr.data;
        } catch {}
      }
      setWorkflowTasks(tasksMap);
    } catch {}
  };
  const fetchClients = async () => { try { const r = await clientsApi.getAll(); setClientsList(r.data || []); } catch {} };

  const handleRoleChange = async (userId, newRole) => {
    try { await teamApi.updateRole(userId, newRole); toast.success('Rol actualizado'); fetchTeam(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const handleDelete = async (userId) => {
    try { await teamApi.delete(userId); toast.success('Miembro eliminado'); fetchTeam(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if (!profileForm.username.trim()) { toast.error('Nombre de usuario requerido'); return; }
    if (!profileForm.password.trim()) { toast.error('Contrasena requerida'); return; }
    try {
      await teamApi.create({ username: profileForm.username.trim().toLowerCase(), password: profileForm.password, name: profileForm.name, role: profileForm.role, position: profileForm.position, associated_client_id: profileForm.role === 'client' ? profileForm.associated_client_id : null });
      setCreateDialogOpen(false);
      setCredentialsDialog({ open: true, email: profileForm.username.trim().toLowerCase(), password: profileForm.password, name: profileForm.name, isReset: false });
      setProfileForm({ name: '', username: '', password: '', role: 'team', position: '', associated_client_id: '' });
      fetchTeam();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear perfil'); }
  };
  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };
  const handleResetPassword = async (userId) => {
    try {
      const r = await teamApi.resetPassword(userId);
      setCredentialsDialog({ open: true, email: r.data.email, password: r.data.password, name: r.data.name, isReset: true });
      toast.success('Contrasena restablecida');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al restablecer contrasena'); }
  };
  const handleEditMember = (member) => {
    setEditingMember(member);
    setEditForm({ name: member.name || '', email: member.username || member.email || '', password: '', role: member.role || 'team', position: member.position || '' });
    setEditDialogOpen(true);
  };
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingMember) return;
    try {
      const payload = { name: editForm.name, role: editForm.role, position: editForm.position };
      if (editForm.password.trim()) payload.password = editForm.password;
      await adminApi.updateUser(editingMember.user_id, payload);
      toast.success('Miembro actualizado');
      setEditDialogOpen(false);
      setEditingMember(null);
      fetchTeam();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al actualizar'); }
  };
  // Role management
  const togglePermission = (key) => setRoleForm(prev => ({ ...prev, permissions: prev.permissions.includes(key) ? prev.permissions.filter(p => p !== key) : [...prev.permissions, key] }));
  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...roleForm };
      if (editingRole) {
        await rolesApi.update(editingRole.role_id, payload);
        toast.success('Rol actualizado');
      } else {
        await rolesApi.create(payload);
        toast.success('Rol creado');
      }
      fetchRoles(); setRoleDialogOpen(false); setEditingRole(null); setRoleForm({ name: '', display_name: '', description: '', permissions: [] });
    } catch { toast.error('Error al guardar rol'); }
  };
  const handleEditRole = (role) => { setEditingRole(role); setRoleForm({ name: role.name, display_name: role.display_name || role.name, description: role.description || '', permissions: role.permissions || [] }); setRoleDialogOpen(true); };
  const handleDeleteRole = async (roleId, roleName) => {
    if (roleName === 'admin' || roleName === 'client') { toast.error('No se puede eliminar el rol de Director ni de Cliente'); return; }
    try { await rolesApi.delete(roleId); toast.success('Rol eliminado'); fetchRoles(); } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Workflow management
  const addWorkflowStep = (member) => {
    setWorkflowForm(prev => ({ ...prev, steps: [...prev.steps, { user_id: member.user_id, name: member.name, role: member.role, action: 'Trabajar' }] }));
  };
  const removeWorkflowStep = (idx) => setWorkflowForm(prev => ({ ...prev, steps: prev.steps.filter((_, i) => i !== idx) }));
  const updateStepAction = (idx, action) => setWorkflowForm(prev => ({ ...prev, steps: prev.steps.map((s, i) => i === idx ? { ...s, action } : s) }));
  const moveStep = (idx, direction) => {
    const newSteps = [...workflowForm.steps];
    const swap = idx + direction;
    if (swap < 0 || swap >= newSteps.length) return;
    [newSteps[idx], newSteps[swap]] = [newSteps[swap], newSteps[idx]];
    setWorkflowForm(prev => ({ ...prev, steps: newSteps }));
  };
  const handleSaveWorkflow = async (e) => {
    e.preventDefault();
    if (!workflowForm.name || workflowForm.steps.length < 2) { toast.error('Nombre y al menos 2 pasos requeridos'); return; }
    try {
      if (editingWorkflow) {
        await workflowsApi.update(editingWorkflow.workflow_id, workflowForm);
      } else {
        await workflowsApi.create(workflowForm);
      }
      toast.success(editingWorkflow ? 'Flujo actualizado' : 'Flujo creado');
      fetchWorkflows(); setWorkflowDialogOpen(false); setEditingWorkflow(null); setWorkflowForm({ name: '', steps: [] });
    } catch { toast.error('Error al guardar flujo'); }
  };
  const handleDeleteWorkflow = async (wfId) => {
    try {
      await workflowsApi.delete(wfId);
      toast.success('Eliminado'); fetchWorkflows();
    } catch { toast.error('Error'); }
  };

  const getRoleDisplay = (roleName) => { const role = roles.find(r => r.name === roleName); return role?.display_name || roleName; };
  const getRoleColor = (roleName) => {
    const map = { admin: 'bg-purple-500/20 text-purple-400', accounting: 'bg-amber-500/20 text-amber-400', production_manager: 'bg-blue-500/20 text-blue-400', team: 'bg-green-500/20 text-green-400', client: 'bg-emerald-500/20 text-emerald-400' };
    return map[roleName] || 'bg-zinc-500/20 text-zinc-400';
  };

  if (loading) return <DashboardLayout title="Gestion de Equipo"><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-orange-500"></div></div></DashboardLayout>;

  const isAdmin = currentUser?.role === 'admin';
  const nonClientTeam = team.filter(m => m.role !== 'client');

  return (
    <DashboardLayout title="Gestion de Equipo">
      <div className="space-y-6" data-testid="team-page">
        {/* Tab Bar */}
        <div className="flex items-center gap-2 border-b border-white/5 pb-3">
          <Button variant={activeTab === 'team' ? 'default' : 'ghost'} onClick={() => setActiveTab('team')} className={activeTab === 'team' ? 'bg-orange-500 hover:bg-orange-600' : 'text-zinc-400'} data-testid="tab-team">
            <Users size={16} className="mr-2" /> Equipo
          </Button>
          {isAdmin && (
            <>
              <Button variant={activeTab === 'roles' ? 'default' : 'ghost'} onClick={() => setActiveTab('roles')} className={activeTab === 'roles' ? 'bg-orange-500 hover:bg-orange-600' : 'text-zinc-400'} data-testid="tab-roles">
                <Shield size={16} className="mr-2" /> Roles
              </Button>
              <Button variant={activeTab === 'workflow' ? 'default' : 'ghost'} onClick={() => setActiveTab('workflow')} className={activeTab === 'workflow' ? 'bg-orange-500 hover:bg-orange-600' : 'text-zinc-400'} data-testid="tab-workflow">
                <GitBranch size={16} className="mr-2" /> Flujograma
              </Button>
            </>
          )}
        </div>

        {/* ===== TEAM TAB ===== */}
        {activeTab === 'team' && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex gap-4">
                <Card className="bg-[#141414] border-white/5"><CardContent className="p-4 flex items-center gap-3"><div className="p-2 rounded-lg bg-purple-500/20"><Shield className="text-purple-400" size={18} /></div><div><p className="text-xs text-zinc-400">Admins</p><p className="text-lg font-bold text-white">{team.filter(m => m.role === 'admin').length}</p></div></CardContent></Card>
                <Card className="bg-[#141414] border-white/5"><CardContent className="p-4 flex items-center gap-3"><div className="p-2 rounded-lg bg-blue-500/20"><UserCog className="text-blue-400" size={18} /></div><div><p className="text-xs text-zinc-400">Equipo</p><p className="text-lg font-bold text-white">{team.filter(m => !['admin','client'].includes(m.role)).length}</p></div></CardContent></Card>
                <Card className="bg-[#141414] border-white/5"><CardContent className="p-4 flex items-center gap-3"><div className="p-2 rounded-lg bg-emerald-500/20"><Users className="text-emerald-400" size={18} /></div><div><p className="text-xs text-zinc-400">Clientes</p><p className="text-lg font-bold text-white">{team.filter(m => m.role === 'client').length}</p></div></CardContent></Card>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-orange-500 hover:bg-orange-600" data-testid="create-profile-btn"><UserPlus size={18} className="mr-2" /> Crear Perfil</Button>
                    </DialogTrigger>
                  <DialogContent className="bg-[#1A1A1A] border-white/10 text-white">
                    <DialogHeader><DialogTitle>Crear Perfil de Colaborador</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreateProfile} className="space-y-4">
                      <div><Label className="text-zinc-300">Nombre Completo</Label><Input value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} required className="bg-[#141414] border-white/10 text-white" data-testid="profile-name-input" /></div>
                      <div><Label className="text-zinc-300">Nombre de Usuario</Label><Input value={profileForm.username} onChange={e => setProfileForm({...profileForm, username: e.target.value.toLowerCase().replace(/\s/g, '')})} required placeholder="Ej: juanperez" className="bg-[#141414] border-white/10 text-white font-mono" data-testid="profile-username-input" /></div>
                      <div>
                        <Label className="text-zinc-300">Contrasena</Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Input type={showPassword ? 'text' : 'password'} value={profileForm.password} onChange={e => setProfileForm({...profileForm, password: e.target.value})} required placeholder="Escribe una contrasena" className="bg-[#141414] border-white/10 text-white pr-10" data-testid="profile-password-input" />
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                          </div>
                          <Button type="button" variant="outline" onClick={() => setProfileForm({...profileForm, password: Math.random().toString(36).slice(-8)})} className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 whitespace-nowrap" data-testid="generate-password-btn">Generar</Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label className="text-zinc-300">Rol/Puesto</Label>
                          <Select value={profileForm.role} onValueChange={v => setProfileForm({...profileForm, role: v})}>
                            <SelectTrigger className="bg-[#141414] border-white/10 text-white"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#1A1A1A] border-white/10">{roles.map(r => <SelectItem key={r.name} value={r.name}>{r.display_name || r.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-zinc-300">Cargo</Label><Input value={profileForm.position} onChange={e => setProfileForm({...profileForm, position: e.target.value})} placeholder="Ej: Disenador" className="bg-[#141414] border-white/10 text-white" /></div>
                      </div>
                      {profileForm.role === 'client' && (
                        <div>
                          <Label className="text-zinc-300">Empresa/Cliente asociado *</Label>
                          <Select value={profileForm.associated_client_id} onValueChange={v => setProfileForm({...profileForm, associated_client_id: v})}>
                            <SelectTrigger className="bg-[#141414] border-white/10 text-white" data-testid="associated-client-select"><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
                            <SelectContent className="bg-[#1A1A1A] border-white/10">{clientsList.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}{c.company ? ` (${c.company})` : ''}</SelectItem>)}</SelectContent>
                          </Select>
                          <p className="text-xs text-zinc-500 mt-1">Varios usuarios pueden asociarse a la misma empresa</p>
                        </div>
                      )}
                      <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="save-profile-btn">Crear Perfil</Button>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              )}
            </div>
            {/* Credentials Dialog */}
            <Dialog open={credentialsDialog.open} onOpenChange={(o) => { if (!o) setCredentialsDialog({ open: false, email: '', password: '', name: '', isReset: false }); }}>
              <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="credentials-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Check size={20} className="text-green-400" /> {credentialsDialog.isReset ? 'Contrasena Restablecida' : 'Colaborador Creado Exitosamente'}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-zinc-400">Comparte estas credenciales con <span className="text-white font-medium">{credentialsDialog.name}</span> para que pueda iniciar sesion:</p>
                  <div className="space-y-3 p-4 rounded-lg bg-[#141414] border border-white/10">
                    <div>
                      <Label className="text-zinc-500 text-xs">Usuario</Label>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-white font-mono text-sm" data-testid="credentials-email">{credentialsDialog.email}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-white" onClick={() => copyToClipboard(credentialsDialog.email, 'email')} data-testid="copy-email-btn">{copiedField === 'email' ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}</Button>
                      </div>
                    </div>
                    <div className="border-t border-white/5 pt-3">
                      <Label className="text-zinc-500 text-xs">Contrasena Temporal</Label>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-white font-mono text-sm" data-testid="credentials-password">{credentialsDialog.password}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-white" onClick={() => copyToClipboard(credentialsDialog.password, 'password')} data-testid="copy-password-btn">{copiedField === 'password' ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}</Button>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full border-orange-500/30 text-orange-400 hover:bg-orange-500/10" onClick={() => { const text = `Usuario: ${credentialsDialog.email}\nContrasena: ${credentialsDialog.password}`; navigator.clipboard.writeText(text); setCopiedField('all'); setTimeout(() => setCopiedField(null), 2000); }} data-testid="copy-all-btn">{copiedField === 'all' ? <><Check size={16} className="mr-2 text-green-400" /> Copiado</> : <><Copy size={16} className="mr-2" /> Copiar Todo</>}</Button>
                  <p className="text-xs text-zinc-500 text-center">Se recomienda que el colaborador cambie su contrasena al iniciar sesion por primera vez</p>
                </div>
              </DialogContent>
            </Dialog>
            {/* Edit Member Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={(o) => { if (!o) { setEditDialogOpen(false); setEditingMember(null); } }}>
              <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="edit-member-dialog">
                <DialogHeader><DialogTitle>Editar Miembro</DialogTitle></DialogHeader>
                <form onSubmit={handleSaveEdit} className="space-y-4">
                  <div><Label className="text-zinc-300">Nombre</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} required className="bg-[#141414] border-white/10 text-white" data-testid="edit-name-input" /></div>
                  <div><Label className="text-zinc-300">Usuario</Label><Input value={editForm.email} disabled className="bg-[#141414] border-white/10 text-zinc-500 font-mono" data-testid="edit-email-input" /></div>
                  <div>
                    <Label className="text-zinc-300">Nueva Contrasena</Label>
                    <p className="text-xs text-zinc-500 mb-1">Deja en blanco para no cambiar la contrasena</p>
                    <Input type="text" value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})} placeholder="Escribir nueva contrasena..." className="bg-[#141414] border-white/10 text-white" data-testid="edit-password-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-zinc-300">Rol</Label>
                      <Select value={editForm.role} onValueChange={v => setEditForm({...editForm, role: v})}>
                        <SelectTrigger className="bg-[#141414] border-white/10 text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">{roles.map(r => <SelectItem key={r.name} value={r.name}>{r.display_name || r.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-zinc-300">Cargo</Label><Input value={editForm.position} onChange={e => setEditForm({...editForm, position: e.target.value})} placeholder="Ej: Disenador" className="bg-[#141414] border-white/10 text-white" data-testid="edit-position-input" /></div>
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="save-edit-btn">Guardar Cambios</Button>
                </form>
              </DialogContent>
            </Dialog>
            <Card className="bg-[#141414] border-white/5">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-400">Miembro</TableHead>
                      <TableHead className="text-zinc-400">Usuario</TableHead>
                      <TableHead className="text-zinc-400">Rol</TableHead>
                      <TableHead className="text-zinc-400">Cargo</TableHead>
                      <TableHead className="text-zinc-400">Registro</TableHead>
                      <TableHead className="text-zinc-400 text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {team.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8"><Users className="mx-auto text-zinc-600 mb-4" size={48} /><p className="text-zinc-400">No hay miembros</p></TableCell></TableRow>
                    ) : team.map((member) => {
                      const isMe = member.user_id === currentUser?.user_id;
                      return (
                        <TableRow key={member.user_id} className="border-white/5" data-testid={`team-member-${member.user_id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="relative group">
                                <Avatar className="h-10 w-10 rounded-full"><AvatarImage src={member.picture ? (member.picture.startsWith('http') ? member.picture : `${API_URL}${member.picture}`) : undefined} className="rounded-full object-cover" /><AvatarFallback className="bg-orange-600 text-white rounded-full">{member.name?.charAt(0) || 'U'}</AvatarFallback></Avatar>
                                {isAdmin && <button onClick={() => { setUploadingForUser(member.user_id); photoInputRef.current?.click(); }} className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"><Camera size={14} className="text-white" /></button>}
                              </div>
                              <div><p className="font-medium text-white">{member.name}{isMe && <span className="text-zinc-500 ml-2">(Tu)</span>}</p>{member.phone && <p className="text-xs text-zinc-500">{member.phone}</p>}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-zinc-400 font-mono text-sm">{member.username || member.email}</TableCell>
                          <TableCell>{isAdmin && !isMe ? (
                            <Select value={member.role} onValueChange={v => handleRoleChange(member.user_id, v)}>
                              <SelectTrigger className={`w-48 border-0 ${getRoleColor(member.role)}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[#1A1A1A] border-white/10">{roles.map(r => <SelectItem key={r.name} value={r.name}>{r.display_name || r.name}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : (<span className={`px-3 py-1 rounded text-sm ${getRoleColor(member.role)}`}>{getRoleDisplay(member.role)}</span>)}</TableCell>
                          <TableCell className="text-zinc-400 text-sm">{member.position || '-'}</TableCell>
                          <TableCell className="text-zinc-400 text-sm">{format(new Date(member.created_at), 'dd MMM yyyy')}</TableCell>
                          <TableCell className="text-right">{isAdmin && !isMe && <div className="flex items-center justify-end gap-1"><Button variant="ghost" size="icon" onClick={() => handleEditMember(member)} className="text-zinc-400 hover:text-blue-400" title="Editar" data-testid={`edit-member-${member.user_id}`}><Pencil size={18} /></Button><Button variant="ghost" size="icon" onClick={() => handleResetPassword(member.user_id)} className="text-zinc-400 hover:text-orange-400" title="Restablecer contrasena" data-testid={`reset-password-${member.user_id}`}><KeyRound size={18} /></Button><Button variant="ghost" size="icon" onClick={() => handleDelete(member.user_id)} className="text-zinc-400 hover:text-red-400" data-testid={`delete-member-${member.user_id}`}><Trash2 size={18} /></Button></div>}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}

        {/* ===== ROLES TAB ===== */}
        {activeTab === 'roles' && (
          <>
            <div className="flex justify-between items-center">
              <p className="text-zinc-400">Administra los roles y permisos del equipo</p>
              <Dialog open={roleDialogOpen} onOpenChange={(o) => { setRoleDialogOpen(o); if (!o) { setEditingRole(null); setRoleForm({ name: '', display_name: '', description: '', permissions: [] }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-role-btn"><Plus size={18} className="mr-2" /> Nuevo Rol</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-lg">
                  <DialogHeader><DialogTitle>{editingRole ? 'Editar Rol' : 'Nuevo Rol'}</DialogTitle></DialogHeader>
                  <form onSubmit={handleRoleSubmit} className="space-y-4">
                    <div><Label className="text-zinc-300">Nombre del Rol (ID)</Label><Input value={roleForm.name} onChange={e => setRoleForm({...roleForm, name: e.target.value})} required disabled={editingRole && (editingRole.name === 'admin' || editingRole.name === 'client')} className="bg-[#141414] border-white/10 text-white" data-testid="role-name-input" /></div>
                    <div><Label className="text-zinc-300">Nombre a Mostrar</Label><Input value={roleForm.display_name} onChange={e => setRoleForm({...roleForm, display_name: e.target.value})} className="bg-[#141414] border-white/10 text-white" /></div>
                    <div><Label className="text-zinc-300">Descripcion</Label><Input value={roleForm.description} onChange={e => setRoleForm({...roleForm, description: e.target.value})} className="bg-[#141414] border-white/10 text-white" /></div>
                    <div>
                      <Label className="text-zinc-300 mb-2 block">Permisos</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {ALL_PERMISSIONS.map(p => (
                          <label key={p.key} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${roleForm.permissions.includes(p.key) ? 'bg-orange-500/20 border border-orange-500/40' : 'bg-[#141414] border border-white/5'}`}>
                            <input type="checkbox" checked={roleForm.permissions.includes(p.key)} onChange={() => togglePermission(p.key)} className="accent-orange-500" />
                            <span className="text-sm text-white">{p.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="save-role-btn">{editingRole ? 'Actualizar' : 'Crear Rol'}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {roles.map(role => (
                <Card key={role.role_id} className="bg-[#141414] border-white/5" data-testid={`role-card-${role.role_id}`}>
                  <CardHeader className="flex flex-row items-start justify-between pb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${role.is_default ? 'bg-orange-500/20' : 'bg-purple-500/20'}`}><Shield className={role.is_default ? 'text-orange-400' : 'text-purple-400'} size={20} /></div>
                      <div><CardTitle className="text-white text-base">{role.display_name || role.name}</CardTitle><p className="text-xs text-zinc-500">{role.name} {role.is_default && '(predeterminado)'}</p></div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEditRole(role)} className="text-zinc-400 hover:text-white h-8 w-8"><Pencil size={14} /></Button>
                      {role.name !== 'admin' && role.name !== 'client' && <Button variant="ghost" size="icon" onClick={() => handleDeleteRole(role.role_id, role.name)} className="text-zinc-400 hover:text-red-400 h-8 w-8"><Trash2 size={14} /></Button>}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {role.description && <p className="text-sm text-zinc-400 mb-3">{role.description}</p>}
                    <div className="flex flex-wrap gap-1">{(role.permissions || []).map(p => (<span key={p} className="px-2 py-0.5 rounded text-xs bg-white/5 text-zinc-300">{p === '*' ? 'Todos' : ALL_PERMISSIONS.find(ap => ap.key === p)?.label || p}</span>))}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* ===== WORKFLOW TAB ===== */}
        {activeTab === 'workflow' && (
          <>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-zinc-400">Configura flujos de trabajo arrastrando personal del directorio</p>
                <p className="text-zinc-500 text-xs mt-1">Ejemplo: Cliente genera orden → Admin asigna → Produccion trabaja → Revision → Aprobacion → Cliente</p>
              </div>
              <Dialog open={workflowDialogOpen} onOpenChange={(o) => { setWorkflowDialogOpen(o); if (!o) { setEditingWorkflow(null); setWorkflowForm({ name: '', steps: [] }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600" data-testid="create-workflow-btn"><Plus size={18} className="mr-2" /> Nuevo Flujo</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editingWorkflow ? 'Editar Flujograma' : 'Nuevo Flujograma de Trabajo'}</DialogTitle></DialogHeader>
                  <form onSubmit={handleSaveWorkflow} className="space-y-5">
                    <div><Label className="text-zinc-300">Nombre del Flujo</Label><Input value={workflowForm.name} onChange={e => setWorkflowForm({...workflowForm, name: e.target.value})} required placeholder="Ej: Flujo de produccion de video" className="bg-[#1A1A1A] border-transparent" data-testid="workflow-name-input" /></div>

                    {/* Team directory to drag from */}
                    <div>
                      <Label className="text-zinc-300 mb-2 block">Directorio de personal (click para agregar al flujo)</Label>
                      <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-[#1A1A1A] border border-white/5">
                        {nonClientTeam.map(m => (
                          <button key={m.user_id} type="button" onClick={() => addWorkflowStep(m)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors bg-[#0D0D0D] border border-white/5 text-zinc-300 hover:border-orange-500/30" data-testid={`add-step-${m.user_id}`}>
                            <Avatar className="h-6 w-6"><AvatarFallback className="bg-orange-600 text-white text-xs">{m.name?.charAt(0)}</AvatarFallback></Avatar>
                            <span>{m.name}</span>
                            <span className="text-[10px] text-zinc-500">({getRoleDisplay(m.role)})</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Workflow steps visualization */}
                    <div>
                      <Label className="text-zinc-300 mb-2 block">Flujo de trabajo ({workflowForm.steps.length} pasos)</Label>
                      <div className="space-y-2">
                        {workflowForm.steps.map((step, idx) => (
                          <div key={idx} className="flex items-center gap-2" data-testid={`workflow-step-${idx}`}>
                            {idx > 0 && <div className="flex items-center text-orange-500 ml-4"><ArrowRight size={16} /></div>}
                            <div className="flex-1 flex items-center gap-3 p-3 rounded-lg bg-[#1A1A1A] border border-white/5">
                              <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center font-bold">{idx + 1}</span>
                              <Avatar className="h-8 w-8"><AvatarFallback className="bg-orange-600 text-white text-sm">{step.name?.charAt(0)}</AvatarFallback></Avatar>
                              <div className="flex-1">
                                <p className="text-sm text-white font-medium">{step.name}</p>
                                <p className="text-[10px] text-zinc-500">{getRoleDisplay(step.role)}</p>
                              </div>
                              <Select value={step.action} onValueChange={v => updateStepAction(idx, v)}>
                                <SelectTrigger className="w-[140px] bg-[#0D0D0D] border-transparent text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#1A1A1A] border-white/10">
                                  <SelectItem value="Recibir">Recibir</SelectItem>
                                  <SelectItem value="Trabajar">Trabajar</SelectItem>
                                  <SelectItem value="Revisar">Revisar</SelectItem>
                                  <SelectItem value="Aprobar">Aprobar</SelectItem>
                                  <SelectItem value="Asignar">Asignar</SelectItem>
                                  <SelectItem value="Entregar">Entregar</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex gap-1">
                                <Button type="button" variant="ghost" size="icon" onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="h-7 w-7 text-zinc-400">&#8593;</Button>
                                <Button type="button" variant="ghost" size="icon" onClick={() => moveStep(idx, 1)} disabled={idx === workflowForm.steps.length - 1} className="h-7 w-7 text-zinc-400">&#8595;</Button>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeWorkflowStep(idx)} className="h-7 w-7 text-zinc-400 hover:text-red-400"><X size={14} /></Button>
                              </div>
                            </div>
                          </div>
                        ))}
                        {workflowForm.steps.length === 0 && <p className="text-zinc-500 text-sm text-center py-6">Agrega miembros del directorio para construir el flujo</p>}
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="save-workflow-btn">{editingWorkflow ? 'Actualizar Flujo' : 'Crear Flujo'}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Existing workflows */}
            <div className="space-y-4">
              {workflows.length === 0 ? (
                <Card className="bg-[#141414] border-white/5"><CardContent className="flex flex-col items-center justify-center py-12"><GitBranch className="text-zinc-600 mb-4" size={48} /><p className="text-zinc-400">No hay flujogramas configurados</p><p className="text-zinc-500 text-sm mt-1">Crea uno para automatizar la distribucion de trabajo</p></CardContent></Card>
              ) : workflows.map(wf => {
                const wfTasks = workflowTasks[wf.workflow_id] || [];
                const activeTasks = wfTasks.filter(t => t.status !== 'done');
                const completedTasks = wfTasks.filter(t => t.status === 'done');
                return (
                <Card key={wf.workflow_id} className="bg-[#141414] border-white/5" data-testid={`workflow-card-${wf.workflow_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{wf.name}</h3>
                        <p className="text-xs text-zinc-500">{wfTasks.length} tarea(s) asignada(s) | {activeTasks.length} activa(s) | {completedTasks.length} completada(s)</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingWorkflow(wf); setWorkflowForm({ name: wf.name, steps: wf.steps }); setWorkflowDialogOpen(true); }} className="text-zinc-400 hover:text-white"><Pencil size={14} className="mr-1" /> Editar</Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteWorkflow(wf.workflow_id)} className="text-zinc-400 hover:text-red-400"><Trash2 size={14} /></Button>
                      </div>
                    </div>
                    {/* Visual flow with task indicators */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-2">
                      {wf.steps.map((step, idx) => {
                        const tasksAtStep = activeTasks.filter(t => (t.current_workflow_step ?? 0) === idx);
                        return (
                        <React.Fragment key={idx}>
                          {idx > 0 && <ArrowRight size={18} className="text-orange-500 flex-shrink-0" />}
                          <div className={cn('flex flex-col items-center gap-1 px-3 py-2 rounded-lg border flex-shrink-0 min-w-[100px]', tasksAtStep.length > 0 ? 'bg-orange-500/10 border-orange-500/30' : 'bg-[#1A1A1A] border-white/5')}>
                            <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                            <p className="text-xs text-white font-medium text-center">{step.name}</p>
                            <p className="text-[10px] text-orange-400">{step.action}</p>
                            {tasksAtStep.length > 0 && (
                              <div className="mt-1 space-y-0.5">
                                {tasksAtStep.map(t => (
                                  <span key={t.task_id} className="block text-[10px] text-orange-300 bg-orange-500/20 px-1.5 py-0.5 rounded truncate max-w-[90px]" title={t.title}>{t.title}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </React.Fragment>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          </>
        )}
        <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
      </div>
    </DashboardLayout>
  );
};

export default TeamPage;
